import React from 'react';
import {
  RadioButton,
  InlineNotification,
  Tag,
  Link,
  SkeletonText,
  SkeletonPlaceholder
} from 'carbon-components-react';
import { Launch16 } from '@carbon/icons-react';
import { AuthorisationItem } from 'types';
import { toLocalDateFormat } from 'utilities/helper.util';

export type AuthorisationType = 'WEB_ENQUIRY_AUTOMATION' | 'CREATE_ENQUIRY';
interface Props {
  isLoading: boolean;
  authorisationType: AuthorisationType;
  onChange: (type: AuthorisationType) => void;
  authorisations?: AuthorisationItem[];
}

const TransactionTypeStep: React.FC<Props> = ({
  isLoading,
  authorisationType,
  onChange,
  authorisations
}) => {
  const isWebFlow = authorisationType === 'WEB_ENQUIRY_AUTOMATION';
  const isApi = authorisationType === 'CREATE_ENQUIRY';
  const existingWebFlowAuth = authorisations?.find(
    (authorisation) =>
      authorisation.authorisationType === 'WEB_ENQUIRY_AUTOMATION'
  );
  const existingApiAuth = authorisations?.find(
    (authorisation) => authorisation.authorisationType === 'CREATE_ENQUIRY'
  );

  return (
    <div className="transaction-type-step">
      <div className="transaction-type-step--heading">
        {isLoading ? (
          <SkeletonText />
        ) : (
          <>
            Choose transaction type
            <Link
              href="https://help.byda.com.au/article/automating-enquiry-lodgement/"
              target="_blank"
            >
              Learn more
              <Launch16 />
            </Link>
          </>
        )}
      </div>
      <div className="transaction-type-step--subheading">
        {isLoading ? (
          <SkeletonText />
        ) : (
          'Select the type of transactions you want to purchase:'
        )}
      </div>

      <div className="transaction-type-step--cards">
        {isLoading ? (
          <>
            <SkeletonPlaceholder />
            <SkeletonPlaceholder />
          </>
        ) : (
          <>
            <button
              type="button"
              className={`transaction-type-step--card${
                isApi ? ' transaction-type-step--card-selected' : ''
              }${!existingApiAuth ? ' transaction-type-step--card-new' : ''}`}
              onClick={() => onChange('CREATE_ENQUIRY')}
              aria-pressed={isApi}
            >
              <div className="transaction-type-step--card-header">
                <RadioButton
                  id="transaction-type-step-radio-api"
                  labelText="API transactions"
                  value="CREATE_ENQUIRY"
                  checked={isApi}
                />
                {existingApiAuth ? (
                  <Tag type="magenta">Top up existing order</Tag>
                ) : (
                  <Tag type="purple">Purchase new transaction</Tag>
                )}
              </div>

              <div className="transaction-type-step--card-desc">
                <div>
                  For automated API enquiries.
                  <br />
                  Purchase transactions for applications that use the BYDA API
                  to lodge enquiries automatically.
                </div>
                <div>
                  One transaction is used each time your application lodges an
                  enquiry.
                </div>
              </div>

              {existingApiAuth && (
                <div className="transaction-type-step--existing-order">
                  <div className="transaction-type-step--existing-order-label">
                    You have an existing transaction order.
                  </div>
                  <div className="transaction-type-step--existing-order-details">
                    <div className="transaction-type-step--existing-order-details-item">
                      <div className="transaction-type-step--existing-order-details-item-label">
                        Transaction left:
                      </div>
                      <div
                        className={`transaction-type-step--existing-order-details-item-value${
                          existingApiAuth.transactionsLeft &&
                          existingApiAuth.transactionsLeft <= 10
                            ? ' red'
                            : ''
                        }`}
                      >
                        {existingApiAuth.transactionsLeft}
                      </div>
                    </div>
                    <div className="transaction-type-step--existing-order-details-item">
                      <div className="transaction-type-step--existing-order-details-item-label">
                        Expires:
                      </div>
                      <div className="transaction-type-step--existing-order-details-item-value">
                        {toLocalDateFormat(existingApiAuth.expires)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <InlineNotification
                kind="info"
                lowContrast={true}
                title=""
                subtitle={
                  existingApiAuth
                    ? 'Select this option to purchase an additional bundle.'
                    : 'Select this to purchase a new transaction bundle.'
                }
                hideCloseButton={true}
              />
            </button>
            <button
              type="button"
              className={`transaction-type-step--card${
                isWebFlow ? ' transaction-type-step--card-selected' : ''
              }${
                !existingWebFlowAuth ? ' transaction-type-step--card-new' : ''
              }`}
              onClick={() => onChange('WEB_ENQUIRY_AUTOMATION')}
              aria-pressed={isWebFlow}
            >
              <div className="transaction-type-step--card-header">
                <RadioButton
                  id="transaction-type-step-radio-web-flow"
                  labelText="Web flow enquiry lodgement"
                  value="WEB_ENQUIRY_AUTOMATION"
                  checked={isWebFlow}
                />
                {existingWebFlowAuth ? (
                  <Tag type="magenta">Top up existing order</Tag>
                ) : (
                  <Tag type="purple">Purchase new transaction</Tag>
                )}
              </div>

              <div className="transaction-type-step--card-desc">
                <div>
                  For automated web-based enquiries.
                  <br />
                  Purchase transactions for applications that use the BYDA web
                  interface to lodge enquiries through an automated workflow.
                </div>
                <div>
                  One transaction is used each time the BYDA web interface is
                  launched from your application.
                </div>
              </div>

              {existingWebFlowAuth && (
                <div className="transaction-type-step--existing-order">
                  <div className="transaction-type-step--existing-order-label">
                    You have an existing transaction order.
                  </div>
                  <div className="transaction-type-step--existing-order-details">
                    <div className="transaction-type-step--existing-order-details-item">
                      <div className="transaction-type-step--existing-order-details-item-label">
                        Transaction left:
                      </div>
                      <div
                        className={`transaction-type-step--existing-order-details-item-value${
                          existingWebFlowAuth.transactionsLeft &&
                          existingWebFlowAuth.transactionsLeft <= 10
                            ? ' red'
                            : ''
                        }`}
                      >
                        {existingWebFlowAuth.transactionsLeft}
                      </div>
                    </div>
                    <div className="transaction-type-step--existing-order-details-item">
                      <div className="transaction-type-step--existing-order-details-item-label">
                        Expires:
                      </div>
                      <div className="transaction-type-step--existing-order-details-item-value">
                        {toLocalDateFormat(existingWebFlowAuth.expires)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <InlineNotification
                kind="info"
                lowContrast={true}
                title=""
                subtitle={
                  existingWebFlowAuth
                    ? 'Select this option to purchase an additional bundle.'
                    : 'Select this to purchase a new transaction bundle.'
                }
                hideCloseButton={true}
              />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionTypeStep;
